import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toDisplayDate } from '@/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useEmployee,
  useDeleteEmployee,
  useEmergencyContacts,
  useDeleteEmergencyContact,
  useEmployeeSensitive,
  useEmployeeDocuments,
  useUploadEmployeeDocument,
  useDeleteEmployeeDocument,
  useHrContext,
} from '@/hooks/use-api'
import { apiClient } from '@/lib/api'
import { useUser } from '@/contexts/UserContext'
import { useRecordBreadcrumb } from '@/contexts/BreadcrumbContext'
import { useToast } from '@/hooks/use-toast'
import { ArrowLeft, Edit, Trash2, Mail, Phone, Plus, AlertCircle } from 'lucide-react'
import { EmployeeForm } from '@/components/hr/EmployeeForm'
import { EmergencyContactForm } from '@/components/hr/EmergencyContactForm'
import type { EmergencyContact, EmploymentStatus, EmployeeDocument } from '@/types/hr'
import { explainError } from '@/lib/explain-error'

function statusBadgeVariant(
  status: EmploymentStatus
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'Active':
      return 'default'
    case 'On Leave':
      return 'secondary'
    case 'Terminated':
      return 'outline'
    default:
      return 'outline'
  }
}

function getInitials(first?: string | null, last?: string | null): string {
  return `${(first ?? '').charAt(0)}${(last ?? '').charAt(0)}`.toUpperCase() || '?'
}

function formatDate(d?: string | null): string {
  if (!d) return '—'
  // toDisplayDate rebuilds DATE-only values (date_of_birth, hire_date, etc.) from
  // their literal Y-M-D so they don't roll back a day in negative-UTC timezones.
  const parsed = toDisplayDate(d)
  if (!parsed) return d
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/** Super-Admin-only view of the sensitive vault (compensation, IDs, banking).
 * Fetched on demand from the dedicated Super-Admin endpoint; never part of the
 * normal employee payload. */
function SensitiveTab({ employeeId }: { employeeId: string }) {
  const { data, isLoading, error } = useEmployeeSensitive(employeeId, true)
  const rows: Array<[string, string | number | null | undefined]> = [
    ['Salary', data?.salary != null ? `${data.salary} ${data.salary_currency ?? ''}`.trim() : null],
    ['Rate', data?.rate],
    ['Payment frequency', data?.payment_frequency],
    ['Salary effective from', data?.salary_effective_from],
    ['SIN number', data?.sin_number],
    ['Tax code', data?.tax_code],
    ['Bank account name', data?.bank_account_name],
    ['Bank account number', data?.bank_account_number],
    ['Transit & institution', data?.bank_transit_institution],
    ['Bank name', data?.bank_name],
    ['Bank branch', data?.bank_branch],
    ['Passport number', data?.passport_number],
    ['Passport country', data?.passport_country],
    ['Passport expiry', data?.passport_expiry],
    ['Work permit number', data?.work_permit_number],
    ['Work permit expiry', data?.work_permit_expiry],
    ['Driving licence number', data?.driving_licence_number],
  ]
  const present = rows.filter(([, v]) => v != null && v !== '')
  return (
    <Card>
      <CardHeader>
        <CardTitle>Sensitive Information</CardTitle>
        <CardDescription>
          Super-Admin only. Compensation, government IDs and banking — encrypted at rest and never
          shown to regular Admins. Access is logged.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
            Loading…
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            Failed to load sensitive data.
          </p>
        )}
        {!isLoading && !error && present.length === 0 && (
          <p className="text-sm text-muted-foreground">No sensitive data on file.</p>
        )}
        {!isLoading && !error && present.length > 0 && (
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            {present.map(([label, value]) => (
              <div key={label}>
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="font-mono">{String(value)}</dd>
              </div>
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  )
}

function formatBytes(n?: number | null): string {
  if (!n) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

/** Super-Admin-only employee documents (offer letters, agreements, and sensitive
 * items). Bytes are stored in GCS and streamed through a gated endpoint. */
function EmployeeDocumentsTab({ employeeId }: { employeeId: string }) {
  const { toast } = useToast()
  const { data, isLoading, error } = useEmployeeDocuments(employeeId, true)
  const uploadDoc = useUploadEmployeeDocument()
  const deleteDoc = useDeleteEmployeeDocument()
  const fileRef = useRef<HTMLInputElement>(null)

  const docs = data ?? []
  const groups = new Map<string, typeof docs>()
  for (const d of docs) {
    if (!groups.has(d.document_type)) groups.set(d.document_type, [])
    groups.get(d.document_type)!.push(d)
  }
  const categories = [...groups.keys()].sort()

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    try {
      await uploadDoc.mutateAsync({ id: employeeId, formData: fd })
      toast({ title: 'Document uploaded' })
    } catch (err: any) {
      toast({
        title: 'Upload failed',
        description: explainError(err, 'Could not upload the file.'),
        variant: 'destructive',
      })
    }
  }

  const download = async (docId: string, name: string) => {
    try {
      const blob = await apiClient.downloadEmployeeDocument(employeeId, docId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = name
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' })
    }
  }

  // In-app preview: fetch the (auth-gated) bytes, type the blob so the browser
  // renders it inline, and show it in a modal. Object URLs are revoked on close.
  const [viewer, setViewer] = useState<{ doc: EmployeeDocument; url: string } | null>(null)
  const [viewerLoadingId, setViewerLoadingId] = useState<string | null>(null)
  const [docDeleteTarget, setDocDeleteTarget] = useState<EmployeeDocument | null>(null)

  const closeViewer = () => {
    setViewer((prev) => {
      if (prev) URL.revokeObjectURL(prev.url)
      return null
    })
  }

  const openViewer = async (doc: EmployeeDocument) => {
    setViewerLoadingId(doc.id)
    try {
      const raw = await apiClient.downloadEmployeeDocument(employeeId, doc.id)
      const typed = new Blob([raw], { type: doc.mime_type || 'application/octet-stream' })
      const url = URL.createObjectURL(typed)
      setViewer((prev) => {
        if (prev) URL.revokeObjectURL(prev.url)
        return { doc, url }
      })
    } catch {
      toast({ title: 'Could not open document', variant: 'destructive' })
    } finally {
      setViewerLoadingId(null)
    }
  }

  // Revoke any outstanding object URL when the tab unmounts.
  useEffect(() => {
    return () => {
      setViewer((prev) => {
        if (prev) URL.revokeObjectURL(prev.url)
        return null
      })
    }
  }, [])

  const mime = viewer?.doc.mime_type ?? ''
  const isPdf = mime === 'application/pdf' || /\.pdf$/i.test(viewer?.doc.name ?? '')
  const isImage = mime.startsWith('image/')
  const isText = mime.startsWith('text/')
  const canPreview = isPdf || isImage || isText

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
          <div>
            <CardTitle>Documents</CardTitle>
            <CardDescription>
              Super-Admin only. Stored securely and streamed on demand. Includes contracts, policies
              and sensitive items.
            </CardDescription>
          </div>
          <div>
            <input
              ref={fileRef}
              type="file"
              className="sr-only"
              aria-hidden="true"
              tabIndex={-1}
              onChange={onFile}
            />
            <Button
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploadDoc.isPending}
            >
              {uploadDoc.isPending ? 'Uploading…' : 'Upload document'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
              Loading…
            </p>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              Failed to load documents.
            </p>
          )}
          {!isLoading && !error && docs.length === 0 && (
            <p className="text-sm text-muted-foreground">No documents yet.</p>
          )}
          {categories.map((cat) => (
            <div key={cat} className="mb-4">
              <h3 className="mb-2 mt-1 border-b-2 border-[#072744]/40 pb-1 text-sm font-bold uppercase tracking-wide text-[#072744]">
                {cat}
              </h3>
              <ul className="divide-y divide-border rounded-md border border-border">
                {groups.get(cat)!.map((d) => (
                  <li
                    key={d.id}
                    className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                  >
                    <button
                      type="button"
                      onClick={() => openViewer(d)}
                      disabled={viewerLoadingId === d.id}
                      className="flex-1 truncate text-left text-primary underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                      aria-label={`View ${d.name}`}
                    >
                      {d.name}
                      {viewerLoadingId === d.id ? (
                        <span className="ml-2 text-xs text-muted-foreground">Opening…</span>
                      ) : d.file_size ? (
                        <span className="ml-2 text-xs text-muted-foreground">
                          {formatBytes(d.file_size)}
                        </span>
                      ) : null}
                    </button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      onClick={() => setDocDeleteTarget(d)}
                      aria-label={`Delete ${d.name}`}
                    >
                      Delete
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && closeViewer()}>
        <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col">
          <DialogHeader>
            <DialogTitle className="truncate pr-6">{viewer?.doc.name}</DialogTitle>
          </DialogHeader>
          <div className="h-[80vh] w-full overflow-auto rounded-md border border-border bg-muted/30">
            {viewer && canPreview ? (
              isImage ? (
                <div className="flex h-full w-full items-center justify-center p-2">
                  <img
                    src={viewer.url}
                    alt={viewer.doc.name}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              ) : (
                <iframe
                  src={viewer.url}
                  title={viewer.doc.name}
                  className="h-full w-full border-0"
                />
              )
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Preview isn’t available for this file type. Download it to view.
                </p>
                {viewer && (
                  <Button size="sm" onClick={() => download(viewer.doc.id, viewer.doc.name)}>
                    Download
                  </Button>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            <div className="flex gap-2">
              {viewer && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => download(viewer.doc.id, viewer.doc.name)}
                >
                  Download
                </Button>
              )}
              {viewer && canPreview && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(viewer.url, '_blank', 'noopener')}
                >
                  Open in new tab
                </Button>
              )}
            </div>
            <Button variant="secondary" size="sm" onClick={closeViewer}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!docDeleteTarget} onOpenChange={(o) => !o && setDocDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>
              “{docDeleteTarget?.name}” will be removed. This can’t be undone from here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (docDeleteTarget) deleteDoc.mutate({ id: employeeId, docId: docDeleteTarget.id })
                setDocDeleteTarget(null)
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentUser } = useUser()
  const { data: hrCtx } = useHrContext()
  const isHrAdmin = hrCtx?.isHrAdmin ?? false
  const { toast } = useToast()

  const { data, isLoading, error } = useEmployee(id ?? '')
  const deleteMutation = useDeleteEmployee()

  const [editOpen, setEditOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [tab, setTab] = useState(searchParams.get('tab') || 'personal')

  const employee = data?.data
  useRecordBreadcrumb(
    employee
      ? `${employee.preferred_name || employee.first_name || ''} ${employee.last_name || ''}`.trim()
      : undefined
  )
  const restricted = data?.restricted === true
  const isSelf =
    (!!hrCtx?.employeeId && hrCtx.employeeId === employee?.id) ||
    employee?.user_id === currentUser?.id
  const canEdit = (isHrAdmin || isSelf) && !restricted

  const handleTabChange = (value: string) => {
    setTab(value)
    setSearchParams((p) => {
      p.set('tab', value)
      return p
    })
  }

  const handleDelete = async () => {
    if (!employee) return
    try {
      await deleteMutation.mutateAsync(employee.id)
      toast({ title: 'Employee deleted' })
      navigate('/hr/employees')
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete employee', variant: 'destructive' })
    }
  }

  if (isLoading) {
    return (
      <main className="container mx-auto p-6">
        <div role="status" aria-live="polite" className="text-center py-12 text-muted-foreground">
          Loading employee…
        </div>
      </main>
    )
  }

  if (error || !employee) {
    return (
      <main className="container mx-auto p-6">
        <div role="alert" className="text-center py-12">
          <AlertCircle className="h-8 w-8 mx-auto mb-2 text-destructive" aria-hidden="true" />
          <p>Employee not found.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/hr/employees')}>
            <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
            Back to directory
          </Button>
        </div>
      </main>
    )
  }

  const display = employee.preferred_name || employee.first_name

  return (
    <main className="container mx-auto p-6 max-w-5xl">
      <Button variant="ghost" size="sm" onClick={() => navigate('/hr/employees')} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
        Back to Employees
      </Button>

      <Card className="mb-4">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            {employee.profile_photo_url ? (
              <img
                src={employee.profile_photo_url}
                alt=""
                className="h-20 w-20 rounded-full object-cover bg-muted"
              />
            ) : (
              <div
                className="h-20 w-20 rounded-full bg-primary/10 text-primary flex items-center justify-center text-2xl font-semibold"
                aria-hidden="true"
              >
                {getInitials(display, employee.last_name)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold flex items-center gap-2 flex-wrap">
                {display} {employee.last_name}
                {employee.pronouns && (
                  <span className="text-sm font-normal text-muted-foreground">
                    ({employee.pronouns})
                  </span>
                )}
                <Badge
                  variant={statusBadgeVariant(employee.employment_status)}
                  aria-label={`Status: ${employee.employment_status}`}
                >
                  {employee.employment_status}
                </Badge>
              </h1>
              {employee.job_title && <p className="text-muted-foreground">{employee.job_title}</p>}
              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                {employee.department_name && (
                  <span className="text-muted-foreground">{employee.department_name}</span>
                )}
                {employee.manager_id && (
                  <span className="text-muted-foreground">
                    Reports to{' '}
                    <Link
                      to={`/hr/employees/${employee.manager_id}`}
                      className="text-primary hover:underline"
                    >
                      {employee.manager_first_name} {employee.manager_last_name}
                    </Link>
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                {employee.work_email && (
                  <a
                    href={`mailto:${employee.work_email}`}
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                    {employee.work_email}
                  </a>
                )}
                {employee.work_phone && (
                  <a
                    href={`tel:${employee.work_phone}`}
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    <Phone className="h-3.5 w-3.5" aria-hidden="true" />
                    {employee.work_phone}
                  </a>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {canEdit && (
                <Button
                  variant="outline"
                  onClick={() => setEditOpen(true)}
                  aria-label="Edit employee"
                >
                  <Edit className="h-4 w-4 mr-2" aria-hidden="true" />
                  Edit
                </Button>
              )}
              {isHrAdmin && (
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirmOpen(true)}
                  aria-label="Delete employee"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            </div>
          </div>

          {restricted && (
            <p className="mt-3 text-xs text-muted-foreground">
              You have directory-level access only. Some fields are hidden.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Profile tabs (personal info, employment, etc.) are hidden for
          directory-level viewers — they see only the top card above. */}
      {!restricted && (
        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="personal">Personal</TabsTrigger>
            <TabsTrigger value="employment">Employment</TabsTrigger>
            <TabsTrigger value="emergency">Emergency Contacts</TabsTrigger>
            <TabsTrigger value="reports">
              Direct Reports
              {employee.direct_reports?.length ? ` (${employee.direct_reports.length})` : ''}
            </TabsTrigger>
            {isHrAdmin && <TabsTrigger value="documents">Documents</TabsTrigger>}
            {isHrAdmin && <TabsTrigger value="sensitive">Sensitive</TabsTrigger>}
          </TabsList>

          <TabsContent value="personal" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Date of birth</dt>
                    <dd>{formatDate(employee.date_of_birth)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Pronouns</dt>
                    <dd>{employee.pronouns || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Personal email</dt>
                    <dd>{employee.personal_email || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Personal phone</dt>
                    <dd>{employee.personal_phone || '—'}</dd>
                  </div>
                  <div className="md:col-span-2">
                    <dt className="text-muted-foreground">Address</dt>
                    <dd>
                      {[
                        employee.address_line1,
                        employee.address_line2,
                        employee.city,
                        employee.state_province,
                        employee.postal_code,
                        employee.country,
                      ]
                        .filter(Boolean)
                        .join(', ') || '—'}
                    </dd>
                  </div>
                  {employee.bio && (
                    <div className="md:col-span-2">
                      <dt className="text-muted-foreground">Bio</dt>
                      <dd className="whitespace-pre-wrap">{employee.bio}</dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="employment" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Employment Details</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Employee number</dt>
                    <dd>{employee.employee_number || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Job title</dt>
                    <dd>{employee.job_title || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Department</dt>
                    <dd>{employee.department_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Manager</dt>
                    <dd>
                      {employee.manager_first_name
                        ? `${employee.manager_first_name} ${employee.manager_last_name}`
                        : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Employment type</dt>
                    <dd>{employee.employment_type || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Work location</dt>
                    <dd>{employee.work_location || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Hire date</dt>
                    <dd>{formatDate(employee.hire_date)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Start date</dt>
                    <dd>{formatDate(employee.start_date)}</dd>
                  </div>
                  {employee.contract_type && (
                    <div>
                      <dt className="text-muted-foreground">Contract type</dt>
                      <dd>{employee.contract_type}</dd>
                    </div>
                  )}
                  {employee.hours_per_week != null && (
                    <div>
                      <dt className="text-muted-foreground">Hours / week</dt>
                      <dd>
                        {employee.hours_per_week}
                        {employee.fte != null ? ` (FTE ${employee.fte})` : ''}
                      </dd>
                    </div>
                  )}
                  {employee.jurisdiction && (
                    <div>
                      <dt className="text-muted-foreground">Jurisdiction</dt>
                      <dd>{employee.jurisdiction}</dd>
                    </div>
                  )}
                  {employee.place_of_work && (
                    <div>
                      <dt className="text-muted-foreground">Place of work</dt>
                      <dd>{employee.place_of_work}</dd>
                    </div>
                  )}
                  {employee.probation_end_date && (
                    <div>
                      <dt className="text-muted-foreground">Probation end</dt>
                      <dd>{formatDate(employee.probation_end_date)}</dd>
                    </div>
                  )}
                  {employee.notice_period && (
                    <div>
                      <dt className="text-muted-foreground">Notice period</dt>
                      <dd>{employee.notice_period}</dd>
                    </div>
                  )}
                  {employee.termination_date && (
                    <>
                      <div>
                        <dt className="text-muted-foreground">Termination date</dt>
                        <dd>{formatDate(employee.termination_date)}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Termination reason</dt>
                        <dd>{employee.termination_reason || '—'}</dd>
                      </div>
                    </>
                  )}
                </dl>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="emergency" className="space-y-4">
            <EmergencyContactsTab employeeId={employee.id} canEdit={canEdit} />
          </TabsContent>

          <TabsContent value="reports" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Direct Reports</CardTitle>
                <CardDescription>People who report to {display}</CardDescription>
              </CardHeader>
              <CardContent>
                {!employee.direct_reports || employee.direct_reports.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No direct reports.</p>
                ) : (
                  <ul className="space-y-2">
                    {employee.direct_reports.map((r) => (
                      <li key={r.id}>
                        <Link
                          to={`/hr/employees/${r.id}`}
                          className="flex items-center gap-3 p-2 rounded hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                          <div
                            className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold"
                            aria-hidden="true"
                          >
                            {getInitials(r.first_name, r.last_name)}
                          </div>
                          <div>
                            <p className="font-medium">
                              {r.first_name} {r.last_name}
                            </p>
                            {r.job_title && (
                              <p className="text-xs text-muted-foreground">{r.job_title}</p>
                            )}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {isHrAdmin && (
            <TabsContent value="documents" className="space-y-4">
              <EmployeeDocumentsTab employeeId={employee.id} />
            </TabsContent>
          )}

          {isHrAdmin && (
            <TabsContent value="sensitive" className="space-y-4">
              <SensitiveTab employeeId={employee.id} />
            </TabsContent>
          )}
        </Tabs>
      )}

      <EmployeeForm open={editOpen} onOpenChange={setEditOpen} employee={employee} />

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this employee?</AlertDialogTitle>
            <AlertDialogDescription>
              {display} {employee.last_name} will be removed from the directory. This is a soft
              delete — the record can be recovered by an administrator.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  )
}

function EmergencyContactsTab({ employeeId, canEdit }: { employeeId: string; canEdit: boolean }) {
  const { data, isLoading } = useEmergencyContacts(employeeId)
  const deleteMutation = useDeleteEmergencyContact()
  const { data: hrCtx } = useHrContext()
  const isHrAdmin = hrCtx?.isHrAdmin ?? false
  const { toast } = useToast()
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<EmergencyContact | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<EmergencyContact | null>(null)

  const contacts = data ?? []

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMutation.mutateAsync({ employeeId, id: deleteTarget.id })
      toast({ title: 'Contact removed' })
    } catch {
      toast({ title: 'Error', description: 'Failed to remove contact', variant: 'destructive' })
    } finally {
      setDeleteTarget(null)
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Emergency Contacts</CardTitle>
          <CardDescription>People to contact in an emergency</CardDescription>
        </div>
        {canEdit && (
          <Button
            size="sm"
            onClick={() => {
              setEditing(null)
              setFormOpen(true)
            }}
          >
            <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
            Add
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p role="status" className="text-sm text-muted-foreground">
            Loading…
          </p>
        ) : contacts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No emergency contacts on file.</p>
        ) : (
          <ul className="space-y-3">
            {contacts.map((c) => (
              <li key={c.id} className="flex items-start justify-between gap-3 p-3 border rounded">
                <div className="flex-1">
                  <p className="font-medium flex items-center gap-2">
                    {c.name}
                    {c.is_primary && (
                      <Badge variant="default" aria-label="Primary contact">
                        Primary
                      </Badge>
                    )}
                  </p>
                  {c.relationship && (
                    <p className="text-sm text-muted-foreground">{c.relationship}</p>
                  )}
                  <div className="flex flex-wrap gap-3 mt-1 text-sm">
                    {c.phone && (
                      <a href={`tel:${c.phone}`} className="text-primary hover:underline">
                        {c.phone}
                      </a>
                    )}
                    {c.email && (
                      <a href={`mailto:${c.email}`} className="text-primary hover:underline">
                        {c.email}
                      </a>
                    )}
                  </div>
                  {c.notes && <p className="text-sm text-muted-foreground mt-1">{c.notes}</p>}
                </div>
                {(canEdit || isHrAdmin) && (
                  <div className="flex gap-1">
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditing(c)
                          setFormOpen(true)
                        }}
                        aria-label={`Edit ${c.name}`}
                      >
                        <Edit className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    )}
                    {isHrAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => setDeleteTarget(c)}
                        aria-label={`Remove ${c.name}`}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <EmergencyContactForm
        open={formOpen}
        onOpenChange={setFormOpen}
        employeeId={employeeId}
        contact={editing}
      />
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this emergency contact?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleteTarget?.name}” will be removed from this employee’s emergency contacts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
